/**
 * Holds the Brooklyn eventLog leg of AI events tracked for a send that starts a
 * new tarot conversation until that conversation has an id: Brooklyn keys the
 * eventLog on `aiSessionId`, which arrives with the request's first streamed chunk.
 *
 * Entries are held under their explicit request id and the signed-in account, and
 * sent once when that request is released under the same account; otherwise never
 * (dropped, or a different account at release). Unknown request ids are ignored.
 *
 * @typedef {{
 *   chatSessionId: string, chatExperienceName: string | null, context: string
 * }} HeldSession
 * @param {object} options
 * @param {(entry: object, session: HeldSession) => void} options.send Sends one held
 *   entry with its conversation's session fields.
 */
export default function createAiEventLogHold ({ send }) {
  /** @type {Map<string, { account: unknown, entries: object[] }>} */
  const requests = new Map()

  return {
    hold (requestId, account, entry) {
      if (!requests.has(requestId)) requests.set(requestId, { account, entries: [] })
      requests.get(requestId).entries.push(entry)
    },

    release (requestId, account, session) {
      const request = requests.get(requestId)
      requests.delete(requestId)
      if (!request || request.account !== account) return
      request.entries.forEach((entry) => send(entry, session))
    },

    drop (requestId) {
      requests.delete(requestId)
    },
  }
}
